import { type TrailCapture } from '@prisma/client'
import * as d3 from 'd3'
import React, { type FC, useEffect, useRef, useState } from 'react'
import { type CaptureData } from '#app/types/capture.ts'
import { binarySearch } from '#app/utils/search.ts'
import { useSmartThumbnailCache } from '#app/utils/smart-cache.tsx'

const CONNECTION_SPEEDS = {
	'slow-2g': 0.5,
	'2g': 0.7,
	'3g': 1.5,
	'4g': 10,
	'5g': 50,
}

export type TooltipX = {
	mouseX: number
	latLng: [number, number]
	elevation: number
	grade: number
	distance: number
	href: string
	thumbnailUrl?: string
}

interface ElevationChartProps {
	trailCapture: Omit<
		TrailCapture,
		'trail' | 'trailId' | 'ownerId' | 'createdAt' | 'updatedAt'
	>
	data: CaptureData
	currentFrameId?: string
	onPositionChange?: (position: TooltipX | null) => void
	navigateTo: (href: string) => void
}

const ElevationChart: FC<ElevationChartProps> = ({
	trailCapture,
	data,
	onPositionChange = () => null,
	currentFrameId,
	navigateTo,
}) => {
	const svgRef = useRef<SVGSVGElement>(null)
	const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
	const smartCache = useSmartThumbnailCache()
	const { schedulePreload, debouncedPreload, getSmartThumbnailUrl } = smartCache

	// Use refs to avoid triggering useEffect when functions change
	const getThumbnailUrlRef = useRef(getSmartThumbnailUrl)
	const debouncedPreloadRef = useRef(debouncedPreload)
	const schedulePreloadRef = useRef(schedulePreload)

	// Update refs when functions change
	useEffect(() => {
		getThumbnailUrlRef.current = getSmartThumbnailUrl
		debouncedPreloadRef.current = debouncedPreload
		schedulePreloadRef.current = schedulePreload
	}, [getSmartThumbnailUrl, debouncedPreload, schedulePreload])

	const marginTop = 5
	const marginRight = 5
	const marginBottom = 60
	const marginLeft = 40

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const { width, height } = entry.contentRect
				setDimensions({ width, height })
			}
		})

		if (svgRef.current) {
			resizeObserver.observe(svgRef.current)
		}

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	useEffect(() => {
		if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0)
			return

		const currentRef = svgRef.current
		const coordinates = data.coordinates
		const elevations = coordinates.map((coordinate) => coordinate[2])
		const cumulativeDistances = coordinates.map((coordinate) => coordinate[4])

		const totalDistance = cumulativeDistances[cumulativeDistances.length - 1]!

		const margin = {
			top: marginTop,
			right: marginRight,
			bottom: marginBottom,
			left: marginLeft,
		}
		const svg = d3.select(currentRef)
		svg.style('cursor', 'pointer')

		const width = dimensions.width - margin.left - margin.right
		const height = dimensions.height - margin.top - margin.bottom

		const xScale = d3.scaleLinear().domain([0, totalDistance]).range([0, width])
		const yScale = d3
			.scaleLinear()
			.domain([d3.min(elevations) || 0, d3.max(elevations) || 0])
			.range([height, 0])

		const lineGenerator = d3
			.line<number>()
			.defined((_, index) => index < cumulativeDistances.length)
			.x((_, index) => xScale(cumulativeDistances[index]!))
			.y((value) => yScale(value))

		const xToTooltip: TooltipX[] = coordinates.map((coordinate) => {
			return {
				mouseX: Math.round(xScale(coordinate[4])),
				latLng: [coordinate[1], coordinate[0]],
				elevation: coordinate[2],
				grade: coordinate[3],
				distance: coordinate[4],
				href: coordinate[5],
			}
		})

		xToTooltip.sort((left, right) => left.mouseX - right.mouseX)

		svg.selectAll('*').remove()

		const chartGroup = svg
			.append('g')
			.attr('transform', `translate(${margin.left},${margin.top})`)

		chartGroup
			.append('path')
			.datum(elevations)
			.attr('fill', 'none')
			.attr('stroke', 'green')
			.attr('stroke-width', 2)
			.attr('d', lineGenerator)

		const xAxisGenerator = d3.axisBottom(xScale).ticks(5)
		chartGroup
			.append('g')
			.attr('transform', `translate(0, ${height})`)
			.call(xAxisGenerator)

		const yAxisGenerator = d3.axisLeft(yScale).ticks(5)
		chartGroup.append('g').call(yAxisGenerator)

		const elevationMarkers = yScale.ticks(3)

		elevationMarkers.forEach((elevation) => {
			chartGroup
				.append('line')
				.attr('x1', 0)
				.attr('y1', yScale(elevation))
				.attr('x2', width)
				.attr('y2', yScale(elevation))
				.attr('stroke', 'gray')
				.attr('stroke-dasharray', '5,5')
		})

		const currentPosition = coordinates.find((coord) => {
			return coord[5].endsWith(`/${currentFrameId}`)
		})

		if (currentPosition) {
			chartGroup
				.append('line')
				.attr('data-testid', 'current-position-line')
				.attr('class', 'current-position-line')
				.attr('stroke', 'dodgerblue')
				.attr('stroke-width', 2)
				.attr('x1', xScale(currentPosition[4]))
				.attr('y1', 0)
				.attr('x2', xScale(currentPosition[4]))
				.attr('y2', height)
		}

		const tooltipGroup = chartGroup
			.append('g')
			.attr('id', 'tooltip-group')
			.attr('data-testid', 'tooltip-group')
			.style('display', 'none')

		tooltipGroup
			.append('rect')
			.attr('id', 'tooltip-background')
			.attr('height', 90)
			.attr('fill', 'white')
			.attr('stroke', '#666')
			.attr('stroke-width', 1)
			.attr('rx', 5)
			.attr('ry', 5)
			.attr('opacity', 0.8)

		tooltipGroup
			.append('image')
			.attr('id', 'thumbnail-image')
			.attr('data-testid', 'thumbnail-image')
			.attr('x', 160)
			.attr('y', 10)
			.attr('width', 150)

		const tooltipText = tooltipGroup
			.append('text')
			.attr('id', 'elevation-tooltip')
			.attr('fill', 'black')

		tooltipText
			.append('tspan')
			.attr('id', 'distance-text')
			.attr('x', 10)
			.attr('dy', '1.5em')

		tooltipText
			.append('tspan')
			.attr('id', 'elevation-text')
			.attr('x', 10)
			.attr('dy', '1.5em')

		tooltipText
			.append('tspan')
			.attr('id', 'grade-text')
			.attr('x', 10)
			.attr('dy', '1.5em')

		tooltipText
			.append('tspan')
			.attr('id', 'instructions')
			.attr('x', 10)
			.attr('dy', '1.5em')

		const mouseoutHandler = (
			_: React.MouseEvent<SVGSVGElement, MouseEvent>,
		) => {
			chartGroup.select('#tooltip-group').style('display', 'none')
			chartGroup.select('.vertical-line').style('display', 'none')
			onPositionChange(null)
		}

		const mouseHandler = async (
			event: React.MouseEvent<SVGSVGElement, MouseEvent>,
		) => {
			const bounds = svg.node()?.getBoundingClientRect() || {
				left: 0,
				top: 0,
				height: 0,
				width: 0,
			}
			const mouseX = event.clientX - bounds.left - margin.left + 5 // Add 5px offset
			const svgWidth = bounds.width - margin.left - margin.right

			const verticalLine = chartGroup.select('.vertical-line')
			verticalLine
				.attr('y1', 0)
				.attr('y2', height)
				.attr('x1', mouseX)
				.attr('x2', mouseX)
				.style('display', null)

			const tooltipGroup = d3.select('#tooltip-group')

			const findNearestMouseX = (dataPoint: { mouseX: number }): number => {
				if (dataPoint.mouseX < mouseX) {
					return -1
				} else if (dataPoint.mouseX > mouseX) {
					return 1
				} else {
					return 0
				}
			}

			const tooltipData = binarySearch(xToTooltip, findNearestMouseX)

			if (tooltipData) {
				const tooltipWidth = 330
				const offsetX = 10
				const tooltipPadding = 10

				let tooltipX = mouseX + offsetX

				// If there isn't enough room on the right, switch to the left of the cursor
				if (mouseX + tooltipWidth + offsetX + tooltipPadding > svgWidth) {
					tooltipX = mouseX - tooltipWidth - offsetX
				}

				tooltipGroup
					.attr('transform', `translate(${tooltipX}, ${margin.top - offsetX})`)
					.style('display', null)

				tooltipGroup.select('#tooltip-background').attr('width', tooltipWidth)

				const frameLocatedAt = tooltipData.href.split('/').pop()!
				const thumbnailUrl = getThumbnailUrlRef.current(
					trailCapture.id,
					frameLocatedAt,
				)

				// Debounced preload for hover behavior (150ms delay)
				debouncedPreloadRef.current(trailCapture.id, frameLocatedAt, 150)

				tooltipGroup.select('#thumbnail-image').attr('href', thumbnailUrl)

				tooltipGroup
					.select('#elevation-tooltip')
					.selectAll('tspan')
					.data([
						'Click now to view',
						`Distance: ${tooltipData.distance}m`,
						`Elevation: ${tooltipData.elevation}m`,
						`Grade: ${tooltipData.grade}%`,
					])
					.join('tspan')
					.attr('x', tooltipPadding)
					.attr('dy', (_, i) => (i === 0 ? '1.5em' : '1.2em'))
					.text((d) => d)

				onPositionChange(tooltipData)
			} else {
				tooltipGroup.style('display', 'none')
				onPositionChange(null)
			}
		}

		const clickHandler = (
			event: React.MouseEvent<SVGSVGElement, MouseEvent>,
		) => {
			const bounds = svg.node()?.getBoundingClientRect() || {
				left: 0,
				top: 0,
				height: 0,
				width: 0,
			}
			const mouseX = event.clientX - bounds.left - margin.left + 5 // Add 5px offset

			const findNearestMouseX = (dataPoint: { mouseX: number }): number => {
				if (dataPoint.mouseX < mouseX) {
					return -1
				} else if (dataPoint.mouseX > mouseX) {
					return 1
				} else {
					return 0
				}
			}

			const tooltipData = binarySearch(xToTooltip, findNearestMouseX)

			if (tooltipData && tooltipData.href) {
				navigateTo(tooltipData.href)
			}
		}

		svg.on('click', clickHandler)
		svg.on('mousemove', mouseHandler)
		svg.on('mouseover', mouseHandler)
		svg.on('mouseout', mouseoutHandler)

		return () => {
			const svg = d3.select(currentRef)
			svg.on('mousemove', null)
			svg.on('mouseover', null)
			svg.on('mouseout', null)
			svg.on('click', null)
			svg.selectAll('*').remove()
		}
	}, [
		trailCapture.id,
		currentFrameId,
		data,
		onPositionChange,
		navigateTo,
		dimensions,
	])

	useEffect(() => {
		if (!data?.coordinates || !smartCache.networkInfo || !smartCache.viewport)
			return

		const speed = CONNECTION_SPEEDS[smartCache.networkInfo.effectiveType] || 1.5
		const hasGoodConnection =
			speed >= 1.5 && smartCache.networkInfo.downlink >= 2
		const hasPointerCapability = window.matchMedia(
			'(hover: hover) and (pointer: fine)',
		).matches

		if (!hasGoodConnection || !hasPointerCapability) return

		console.log(
			`Preloading ${data.coordinates.length} thumbnails (${smartCache.networkInfo.effectiveType}, ${smartCache.networkInfo.downlink}Mbps)`,
		)

		data.coordinates.forEach((coordinate, index) => {
			const frameId = coordinate[5].split('/').pop()
			if (frameId) {
				const delay = index * 300
				schedulePreloadRef.current(trailCapture.id, frameId, delay)
			}
		})
	}, [trailCapture.id, data, smartCache.networkInfo, smartCache.viewport])

	return (
		<svg
			data-testid="elevation-chart"
			ref={svgRef}
			width="100%"
			height="100%"
			preserveAspectRatio="none"
			viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
		></svg>
	)
}

export default ElevationChart
